import React, { useState, useEffect } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { Info, X, ExternalLink, Save, Plus } from 'lucide-react';

const UtilityContextView = () => {
  const { selectedItem, setSelectedItem, updateLead, updatePortfolioItem } = useAppState();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [tagInput, setTagInput] = useState('');

  // Reset edit mode when selected item changes
  useEffect(() => {
    if (selectedItem) {
      if ('businessName' in selectedItem) {
        // Lead
        setEditData({
          businessName: selectedItem.businessName || '',
          niche: selectedItem.niche || '',
          category: selectedItem.category || '',
          status: selectedItem.status || 'Research',
          notes: selectedItem.notes || ''
        });
      } else {
        // Portfolio Item
        setEditData({
          title: selectedItem.title || '',
          url: selectedItem.url || '',
          description: selectedItem.description || '',
          category: selectedItem.category || '',
          tags: selectedItem.tags || []
        });
      }
      setEditMode(false);
      setTagInput('');
    }
  }, [selectedItem]);

  if (!selectedItem) {
    return (
      <div className="utility-section" data-testid="utility-context-view">
        <div className="utility-header">
          <Info size={16} className="utility-icon" />
          <h3 className="utility-title">Context</h3>
        </div>
        <div className="context-empty">
          <p>Select an item or lead to view details</p>
        </div>
      </div>
    );
  }

  const isLead = 'businessName' in selectedItem;

  const handleSave = async () => {
    if (isLead) {
      await updateLead(selectedItem.id, editData);
    } else {
      await updatePortfolioItem(selectedItem.id, editData);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    if (isLead) {
      setEditData({
        businessName: selectedItem.businessName || '',
        niche: selectedItem.niche || '',
        category: selectedItem.category || '',
        status: selectedItem.status || 'Research',
        notes: selectedItem.notes || ''
      });
    } else {
      setEditData({
        title: selectedItem.title || '',
        url: selectedItem.url || '',
        description: selectedItem.description || '',
        category: selectedItem.category || '',
        tags: selectedItem.tags || []
      });
    }
    setEditMode(false);
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !editData.tags.includes(tagInput.trim())) {
      setEditData({ ...editData, tags: [...editData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setEditData({ ...editData, tags: editData.tags.filter(tag => tag !== tagToRemove) });
  };

  const STAGES = ['Research', 'Identified', 'Contacted', 'Conversation', 'Proposal', 'Won', 'Lost'];

  return (
    <div className="utility-section" data-testid="utility-context-view">
      <div className="utility-header">
        <Info size={16} className="utility-icon" />
        <h3 className="utility-title">{isLead ? 'Lead Details' : 'Context'}</h3>
        <button
          onClick={() => setSelectedItem(null)}
          className="context-close"
          data-testid="close-context-btn"
        >
          <X size={14} />
        </button>
      </div>
      <div className="context-content">
        {isLead ? (
          <>
            {!editMode ? (
              <>
                <div className="context-field">
                  <label className="context-label">Business</label>
                  <div className="context-value">{selectedItem.businessName}</div>
                </div>
                {selectedItem.category && (
                  <div className="context-field">
                    <label className="context-label">Category</label>
                    <div className="context-value-badge">{selectedItem.category}</div>
                  </div>
                )}
                <div className="context-field">
                  <label className="context-label">Niche</label>
                  <div className="context-value">{selectedItem.niche || 'Not specified'}</div>
                </div>
                <div className="context-field">
                  <label className="context-label">Status</label>
                  <div className="context-value">{selectedItem.status}</div>
                </div>
                <div className="context-field">
                  <label className="context-label">Notes</label>
                  <div className="context-value context-notes">{selectedItem.notes || 'No notes'}</div>
                </div>
                <button 
                  className="context-edit-btn"
                  data-testid="edit-lead-btn"
                  onClick={() => setEditMode(true)}
                >
                  Edit Lead
                </button>
              </>
            ) : (
              <div className="context-edit-form">
                <div className="context-field">
                  <label className="context-label">Business Name</label>
                  <input
                    type="text"
                    value={editData.businessName}
                    onChange={(e) => setEditData({ ...editData, businessName: e.target.value })}
                    className="context-input"
                    data-testid="edit-business-input"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Category</label>
                  <input
                    type="text"
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    className="context-input"
                    data-testid="edit-category-input"
                    placeholder="e.g., Quick Website, Full Rebrand"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Niche/Industry</label>
                  <input
                    type="text"
                    value={editData.niche}
                    onChange={(e) => setEditData({ ...editData, niche: e.target.value })}
                    className="context-input"
                    data-testid="edit-niche-input"
                    placeholder="e.g., E-commerce, SaaS"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    className="context-select"
                    data-testid="edit-status-select"
                  >
                    {STAGES.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
                <div className="context-field">
                  <label className="context-label">Notes</label>
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    className="context-textarea"
                    data-testid="edit-notes-textarea"
                    placeholder="Add notes..."
                    rows="4"
                  />
                </div>
                <div className="context-edit-actions">
                  <button 
                    className="context-save-btn"
                    data-testid="save-lead-btn"
                    onClick={handleSave}
                  >
                    <Save size={14} />
                    Save
                  </button>
                  <button 
                    className="context-cancel-btn"
                    data-testid="cancel-edit-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {!editMode ? (
              <>
                <div className="context-field">
                  <label className="context-label">Title</label>
                  <div className="context-value">{selectedItem.title}</div>
                </div>
                {selectedItem.category && (
                  <div className="context-field">
                    <label className="context-label">Category</label>
                    <div className="context-value-badge">{selectedItem.category}</div>
                  </div>
                )}
                <div className="context-field">
                  <label className="context-label">URL</label>
                  <a
                    href={selectedItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="context-link"
                    data-testid="context-url-link"
                  >
                    <span className="context-link-text">{selectedItem.url}</span>
                    <ExternalLink size={12} />
                  </a>
                </div>
                {selectedItem.description && (
                  <div className="context-field">
                    <label className="context-label">Description</label>
                    <div className="context-value context-notes">{selectedItem.description}</div>
                  </div>
                )}
                {selectedItem.tags && selectedItem.tags.length > 0 && (
                  <div className="context-field">
                    <label className="context-label">Tags</label>
                    <div className="context-tags-display">
                      {selectedItem.tags.map((tag, index) => (
                        <span key={index} className="context-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                <button 
                  className="context-edit-btn"
                  data-testid="edit-portfolio-btn"
                  onClick={() => setEditMode(true)}
                >
                  Edit Portfolio Item
                </button>
              </>
            ) : (
              <div className="context-edit-form">
                <div className="context-field">
                  <label className="context-label">Title</label>
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="context-input"
                    data-testid="edit-portfolio-title-input"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">URL</label>
                  <input
                    type="url"
                    value={editData.url}
                    onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                    className="context-input"
                    data-testid="edit-portfolio-url-input"
                    placeholder="https://example.com"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Category</label>
                  <input
                    type="text"
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    className="context-input"
                    data-testid="edit-portfolio-category-input"
                    placeholder="e.g., Web Design, Branding"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="context-textarea"
                    data-testid="edit-portfolio-description-textarea"
                    placeholder="Brief description..."
                    rows="3"
                  />
                </div>
                <div className="context-field">
                  <label className="context-label">Tags</label>
                  <div className="context-tag-input-container">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tag..."
                      className="context-input"
                      data-testid="edit-portfolio-tag-input"
                    />
                    <button 
                      type="button" 
                      onClick={addTag} 
                      className="context-tag-add-btn"
                      data-testid="add-portfolio-tag-btn"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {editData.tags && editData.tags.length > 0 && (
                    <div className="context-tags-edit">
                      {editData.tags.map((tag, index) => (
                        <span key={index} className="context-tag-editable" data-testid={`portfolio-tag-${tag}`}>
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="context-tag-remove"
                            data-testid={`remove-portfolio-tag-${tag}`}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="context-edit-actions">
                  <button 
                    className="context-save-btn"
                    data-testid="save-portfolio-btn"
                    onClick={handleSave}
                  >
                    <Save size={14} />
                    Save
                  </button>
                  <button 
                    className="context-cancel-btn"
                    data-testid="cancel-portfolio-edit-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UtilityContextView;
